import {
  InvoiceColumn,
  OperationStatus,
  OperationStatusLabel,
  UploadLabel,
  UploadType,
} from '@/context/services/invoice/type';
import { ErrorMessagesProvider } from '@/features/invoices/components/Details/Components/Context/ErrorMessagesProvider';
import { OperationStatusProvider } from '@/features/invoices/components/Details/Components/Context/OperationStatus';
import { TimestampStatusProvider } from '@/features/invoices/components/Details/Components/Context/TimestampStatus';
import { FileNameCell } from '@/features/invoices/components/List/Table/Columns/Cells/FileNameCell';
import { InvoicePICCell } from '@/features/invoices/components/List/Table/Columns/Cells/InvoicePICCell';
import { useEnhanceInvoiceListSearchFlag } from '@/hooks/feature_flag/useEnhanceInvoiceListSearchFlag';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { useTranslation } from '@/i18n';
import { splitFileNameExtension } from '@/libs/files/split';
import { isNumber } from '@/libs/typeguard/number';
import type { ColumnType } from 'antd/es/table/interface';
import classnames from 'classnames/bind';
import { DATE_FORMAT, format, formatDateWithTimeNoSecond } from 'date-util';
import { Dayjs } from 'dayjs';
import { MemoCell } from './Cells/MemoCell';
import styles from './columns.module.scss';

const cx = classnames.bind(styles);

export const useColumns = (): ColumnType<InvoiceColumn>[] => {
  const { t } = useTranslation();
  const flagEnhanceInvoiceListSearch = useGetFlags(
    'ENHANCE_INVOICE_LIST_SEARCH'
  );
  const flagInvoiceListDueDateAmount = useEnhanceInvoiceListSearchFlag();

  const allColumns: (ColumnType<InvoiceColumn> & { hidden?: boolean })[] = [
    {
      title: t('received_invoice_id'),
      dataIndex: 'id',
      key: 'id',
      render(value) {
        return `IV-${value}`;
      },
      width: 72,
      className: cx(styles['receivedInvoiceIdTd']),
    },
    {
      title: t('ファイル名'),
      dataIndex: 'fileName',
      key: 'fileName',
      render(value: string, r: InvoiceColumn) {
        const { name, ext } = splitFileNameExtension(value ?? '');
        return (
          <OperationStatusProvider value={r.operationStatus}>
            <TimestampStatusProvider value={r.timestampStatus}>
              <ErrorMessagesProvider value={r?.errorMessages}>
                <FileNameCell
                  name={name}
                  ext={ext}
                  aiSuggestStatus={r.aiSuggestStatus}
                />
              </ErrorMessagesProvider>
            </TimestampStatusProvider>
          </OperationStatusProvider>
        );
      },
      className: cx(styles['fileTd']),
    },
    {
      title: (
        <>
          <div>{t('支払先コード')}</div>
          <div>{t('label_ap_payee')}</div>
        </>
      ),
      key: 'payeeCode',
      dataIndex: 'payeeCode',
      render(value, record) {
        return (
          <div>
            <div className={cx(styles['payee'])}>{value}</div>
            <div className={cx(styles['payee'])}>{record.payeeName}</div>
          </div>
        );
      },
      className: cx(styles['payeeContainer']),
    },
    {
      title: t('received_invoice_due_date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      render(value?: string | null) {
        return value ? format(value, DATE_FORMAT) : '';
      },
      width: '120px',
      hidden: !flagInvoiceListDueDateAmount,
    },
    {
      title: t('received_invoice_total_amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render(value?: string | null) {
        return isNumber(value) ? `${parseInt(value!).toLocaleString()}円` : '';
      },
      width: '120px',
      align: 'right',
      hidden: !flagInvoiceListDueDateAmount,
    },
    {
      title: t('請求書担当者'),
      key: 'pics',
      render(_, record) {
        const picNames = record.pics.map((pic) => pic.name).join(',');
        return <InvoicePICCell value={picNames} />;
      },
      className: cx(styles['invoicePicTd']),
    },
    {
      title: (
        <>
          <div>{t('uploader')}</div>
          <div>{t('アップロード時刻')}</div>
        </>
      ),
      dataIndex: 'uploadedAt',
      render(value: Dayjs, record: InvoiceColumn) {
        return (
          <div>
            <div>{record.uploader?.name ?? '--'}</div>
            <div>{formatDateWithTimeNoSecond(value)}</div>
          </div>
        );
      },
      width: '136px',
      className: cx(styles['uploaderTimeTd']),
    },
    {
      title: t('受領方法'),
      dataIndex: 'uploadType',
      render(value: UploadType) {
        return <div>{UploadLabel[value]}</div>;
      },
      width: '88px',
      className: cx(styles['receivingMethodTd']),
    },
    {
      title: (
        <>
          <div>{t('作業')}</div>
          <div>{t('ステータス')}</div>
        </>
      ),
      dataIndex: 'operationStatus',
      render(value: OperationStatus) {
        return OperationStatusLabel[value];
      },
      width: '110px',
      hidden: flagEnhanceInvoiceListSearch,
    },
    {
      title: t('メモ'),
      dataIndex: 'memo',
      render(value) {
        return <MemoCell value={value} />;
      },
      className: cx(styles['memoTd']),
    },
  ];

  return allColumns.filter((column) => !column.hidden);
};
